import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

let tokenGetter = async () => null;

export function setAuthTokenGetter(fn) {
  tokenGetter = fn;
}

async function authHeaders() {
  const token = await tokenGetter();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(method, path, data, config = {}) {
  const headers = {
    ...(await authHeaders()),
    ...(config.headers || {}),
  };
  try {
    const res = await axios({
      method,
      url: `${API_URL}${path}`,
      data,
      ...config,
      headers,
    });
    return res.data;
  } catch (err) {
    const message = err.response?.data?.error || err.message || "Request failed";
    const error = new Error(message);
    error.status = err.response?.status;
    error.data = err.response?.data;
    throw error;
  }
}

export const instructorApi = {
  sync: (body) => request("post", "/api/v1/auth/sync", body || {}),
  me: () => request("get", "/api/v1/auth/me"),
  sendVerificationEmail: () => request("post", "/api/v1/auth/send-verification"),
  getInstructor: () => request("get", "/api/v1/instructor/me"),
  preview: () => request("get", "/api/v1/instructor/me/preview"),
  patchProfile: (body) => request("patch", "/api/v1/instructor/me", body),
  generateBio: () => request("post", "/api/v1/instructor/me/generate-bio"),
  saveStep: (step, body) => request("put", `/api/v1/instructor/me/steps/${step}`, body),
  submit: () => request("post", "/api/v1/instructor/me/submit"),
  submitChanges: () => request("post", "/api/v1/instructor/me/submit-changes"),
  withdraw: () => request("post", "/api/v1/instructor/me/withdraw"),
  listCredentials: () => request("get", "/api/v1/instructor/me/credentials"),
  createCredential: (body) => request("post", "/api/v1/instructor/me/credentials", body),
  updateCredential: (id, body) =>
    request("patch", `/api/v1/instructor/me/credentials/${id}`, body),
  deleteCredential: (id) => request("delete", `/api/v1/instructor/me/credentials/${id}`),
  listOfferings: () => request("get", "/api/v1/instructor/me/offerings"),
  createOffering: (body) => request("post", "/api/v1/instructor/me/offerings", body),
  updateOffering: (id, body) => request("patch", `/api/v1/instructor/me/offerings/${id}`, body),
  deleteOffering: (id) => request("delete", `/api/v1/instructor/me/offerings/${id}`),
  signUpload: (body) => request("post", "/api/v1/instructor/uploads/sign", body),
  directUpload: (body) => request("post", "/api/v1/instructor/uploads/direct", body),
  completeUpload: (assetId) =>
    request("post", `/api/v1/instructor/uploads/${assetId}/complete`),
  listAssets: (kind) =>
    request("get", "/api/v1/instructor/me/assets", undefined, {
      params: kind ? { kind } : undefined,
    }),
  deleteAsset: (id) => request("delete", `/api/v1/instructor/me/assets/${id}`),
  adminList: (status) =>
    request("get", "/api/v1/admin/applications", undefined, {
      params: status ? { status } : undefined,
    }),
  adminGet: (id) => request("get", `/api/v1/admin/applications/${id}`),
  adminClaim: (id) => request("post", `/api/v1/admin/applications/${id}/claim`),
  adminApprove: (id) => request("post", `/api/v1/admin/applications/${id}/approve`),
  adminReject: (id, reason) =>
    request("post", `/api/v1/admin/applications/${id}/reject`, { reason }),
  adminSuspend: (id, reason) =>
    request("post", `/api/v1/admin/applications/${id}/suspend`, { reason }),
  adminReinstate: (id) => request("post", `/api/v1/admin/applications/${id}/reinstate`),
};

const EXT_MIME = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  bmp: "image/bmp",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  tif: "image/tiff",
  tiff: "image/tiff",
  pdf: "application/pdf",
};

function inferMime(file) {
  const raw = String(file.type || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  if (raw === "image/jpg" || raw === "image/pjpeg") return "image/jpeg";
  if (raw === "image/x-ms-bmp") return "image/bmp";
  if (raw && raw !== "application/octet-stream") return raw;
  const ext = String(file.name || "")
    .split(".")
    .pop()
    ?.toLowerCase();
  return (ext && EXT_MIME[ext]) || raw || "application/octet-stream";
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

/** Re-encode images to JPEG so HEIC/odd MIME types work in browsers that can decode them. */
function reencodeImageToJpeg(file, maxEdge = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        const scale = Math.min(1, maxEdge / Math.max(width, height));
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
              reject(new Error("Could not process image. Try a JPG or PNG file."));
              return;
            }
            resolve(
              new File([blob], (file.name || "photo").replace(/\.[^.]+$/, ".jpg"), {
                type: "image/jpeg",
              })
            );
          },
          "image/jpeg",
          quality
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error(
          "Could not read this image. If it is an iPhone HEIC photo, export it as JPG and try again."
        )
      );
    };
    img.src = url;
  });
}

/**
 * Upload profile photos / certificates.
 * Uses direct base64 upload (works without GCS). Photos are re-encoded to JPEG first.
 */
export async function uploadInstructorFile(file, kind) {
  if (!file) throw new Error("No file selected");

  let uploadFile = file;
  const mime = inferMime(file);
  const isImage =
    mime.startsWith("image/") ||
    /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif|tiff?)$/i.test(file.name || "");

  // Profile/cover/gallery photos: always convert to JPEG so HEIC/GIF/BMP/AVIF/etc. work.
  if (kind === "profile_photo" || kind === "cover" || kind === "gallery") {
    if (!isImage) {
      throw new Error("Please choose an image file (JPG, PNG, WebP, GIF, HEIC, and similar).");
    }
    try {
      uploadFile = await reencodeImageToJpeg(file);
    } catch (err) {
      if (mime === "image/heic" || mime === "image/heif" || /\.hei[cf]$/i.test(file.name || "")) {
        throw new Error(
          "This iPhone HEIC photo could not be converted in the browser. Export as JPG or PNG and try again."
        );
      }
      // Already a standard format — upload original if canvas conversion fails
      if (["image/jpeg", "image/png", "image/webp"].includes(mime)) {
        uploadFile = file;
      } else {
        throw (
          err ||
          new Error("Could not process this image. Try JPG, PNG, or WebP.")
        );
      }
    }
  } else if (kind === "certificate" && mime.startsWith("image/")) {
    try {
      uploadFile = await reencodeImageToJpeg(file);
    } catch {
      uploadFile = file;
    }
  }

  if (kind === "certificate" && !uploadFile) {
    throw new Error("Please choose a certificate PDF or image");
  }

  const contentType = inferMime(uploadFile);
  const data = await fileToBase64(uploadFile);

  const result = await instructorApi.directUpload({
    kind,
    contentType,
    size: uploadFile.size,
    filename: uploadFile.name || file.name,
    data,
  });

  if (!result?.asset) {
    throw new Error("Upload failed. Please try again.");
  }
  return result.asset;
}
